const LOCALE = document.documentElement.lang || undefined;

const capitalizeFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const buildMonths = () => Array.from({ length: 12 }, (_, i) => capitalizeFirst(
  new Intl.DateTimeFormat(LOCALE, { month: 'long' }).format(
    new Date(2000, i, 1),
  ),
));

const buildWeekdays = () => Array.from({ length: 7 }, (_, i) => capitalizeFirst(
  new Intl.DateTimeFormat(LOCALE, { weekday: 'short' }).format(
    new Date(2000, 0, 2 + i),
  ),
));

export default class DatePicker {
  static MONTHS = buildMonths();

  static WEEKDAYS = buildWeekdays();

  constructor({
    input, pill, root, mount,
  }) {
    this.input = input;
    this.pill = pill;
    this.root = root;
    this.mount = mount || root;
    this.container = this.root.parentElement || this.root;

    this.availableDates = DatePicker.parseAvailableDates(
      this.root.dataset.availableDates,
    );
    this.minYear = parseInt(this.root.dataset.minYear, 10) || 2000;
    this.maxYear = parseInt(this.root.dataset.maxYear, 10) || 2100;

    const parsedInitial = this.input.value
      ? DatePicker.parseISOToLocalDate(this.input.value)
      : null;
    this.selectedDate = parsedInitial;
    this.visibleYear = this.selectedDate
      ? this.selectedDate.getFullYear()
      : new Date().getFullYear();
    this.visibleMonth = this.selectedDate
      ? this.selectedDate.getMonth()
      : new Date().getMonth();
    this.pendingMonth = this.visibleMonth;
    this.mode = 'day';

    this.build();
    this.render();

    if (this.selectedDate) {
      this.syncPillLabel();
    }
  }

  setSelectedDate(iso) {
    const date = DatePicker.parseISOToLocalDate(iso);
    if (!date) return;

    this.selectedDate = date;
    this.visibleYear = date.getFullYear();
    this.visibleMonth = date.getMonth();
    this.pendingMonth = this.visibleMonth;
    this.input.value = iso;
    this.syncPillLabel();

    if (!this.picker.hidden) {
      this.render();
    }
  }

  static parseAvailableDates(raw) {
    if (!raw || raw.trim() === '') {
      return null;
    }

    const set = new Set(
      raw
        .split(',')
        .map((value) => value.trim())
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value)),
    );

    return set.size ? set : null;
  }

  static formatISO(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  static formatDisplay(date) {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  static parseISOToLocalDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return null;
    }

    const [year, month, day] = value
      .split('-')
      .map((part) => parseInt(part, 10));
    const date = new Date(year, month - 1, day);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  }

  isAvailable(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date <= today) return false;

    if (!this.availableDates) {
      return true;
    }

    return this.availableDates.has(DatePicker.formatISO(date));
  }

  build() {
    this.picker = document.createElement('div');
    this.picker.className = 'c-booking-widget__calendar';
    this.picker.hidden = true;

    this.picker.innerHTML = `
      <div class="c-booking-widget__calendar-header">
        <button type="button" class="c-booking-widget__calendar-title" data-switch="month-year" aria-label="Select month and year">
          <span></span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M8.25 4.5L15.75 12L8.25 19.5" stroke="currentcolor" stroke-width="0.875" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="c-booking-widget__calendar-navs">
          <button type="button" class="c-booking-widget__calendar-nav" data-nav="prev" aria-label="Previous month">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M15.75 19.5L8.25 12L15.75 4.5" stroke="currentcolor" stroke-width="0.875" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <button type="button" class="c-booking-widget__calendar-nav" data-nav="next" aria-label="Next month">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M8.25 4.5L15.75 12L8.25 19.5" stroke="currentcolor" stroke-width="0.875" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>
      <div class="c-booking-widget__calendar-body"></div>
    `;

    this.mount.appendChild(this.picker);

    this.body = this.picker.querySelector('.c-booking-widget__calendar-body');
    this.title = this.picker.querySelector('.c-booking-widget__calendar-title');

    this.picker.addEventListener('click', this.handlePickerClick);
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleEscape);
  }

  toggleOpen = () => {
    if (this.picker.hidden) {
      this.open();
    } else {
      this.close();
    }
  };

  open = () => {
    this.picker.hidden = false;
    this.root.classList.add('is-open');
    this.render();
  };

  close = () => {
    this.picker.hidden = true;
    this.root.classList.remove('is-open');
    this.mode = 'day';
    this.pendingMonth = this.visibleMonth;
  };

  handleDocumentClick = (event) => {
    if (this.picker.hidden) {
      return;
    }

    if (!this.container.contains(event.target)) {
      this.close();
    }
  };

  handleEscape = (event) => {
    if (event.key === 'Escape' && !this.picker.hidden) {
      this.close();
    }
  };

  handlePickerClick = (event) => {
    event.stopPropagation();

    const target = event.target.closest('button');

    if (!target) {
      return;
    }

    const {
      nav, monthIndex, yearValue, iso,
    } = target.dataset;

    if (nav) {
      const delta = nav === 'next' ? 1 : -1;
      const next = new Date(this.visibleYear, this.visibleMonth + delta, 1);
      this.visibleYear = next.getFullYear();
      this.visibleMonth = next.getMonth();
      this.pendingMonth = this.visibleMonth;
      this.mode = 'day';
      this.render();
      this.root.dispatchEvent(
        new CustomEvent('datepicker:monthchange', {
          bubbles: true,
          detail: { year: this.visibleYear, month: this.visibleMonth },
        }),
      );
      return;
    }

    if (target.dataset.switch === 'month-year') {
      this.mode = 'month';
      this.pendingMonth = this.visibleMonth;
      this.render();
      return;
    }

    if (monthIndex !== undefined) {
      this.pendingMonth = parseInt(monthIndex, 10);
      this.mode = 'year';
      this.render();
      return;
    }

    if (yearValue !== undefined) {
      this.visibleYear = parseInt(yearValue, 10);
      this.visibleMonth = this.pendingMonth;
      this.mode = 'day';
      this.render();
      this.root.dispatchEvent(
        new CustomEvent('datepicker:monthchange', {
          bubbles: true,
          detail: { year: this.visibleYear, month: this.visibleMonth },
        }),
      );
      return;
    }

    if (iso) {
      const selected = DatePicker.parseISOToLocalDate(iso);
      if (selected && this.isAvailable(selected)) {
        this.selectedDate = selected;
        this.visibleYear = selected.getFullYear();
        this.visibleMonth = selected.getMonth();
        this.pendingMonth = this.visibleMonth;
        this.input.value = iso;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncPillLabel();
        this.close();
      }
    }
  };

  syncPillLabel() {
    const label = this.selectedDate
      ? DatePicker.formatDisplay(this.selectedDate)
      : 'Select Date';
    const span = this.pill.querySelector('span');
    if (span) {
      span.textContent = label;
    } else {
      this.pill.textContent = label;
    }

    this.pill.classList.add('has-date');
  }

  renderHeader() {
    this.title.querySelector('span').textContent = `${
      DatePicker.MONTHS[this.visibleMonth]
    } ${this.visibleYear}`;
  }

  render() {
    this.picker.dataset.mode = this.mode;
    this.renderHeader();

    if (this.mode === 'month') {
      this.renderMonthGrid();
      return;
    }

    if (this.mode === 'year') {
      this.renderYearGrid();
      return;
    }

    this.renderDayGrid();
  }

  renderMonthGrid() {
    this.body.innerHTML = `
      <div class="c-booking-widget__calendar-grid c-booking-widget__calendar-grid--months">
        ${DatePicker.MONTHS.map(
    (month, index) => `
          <button type="button" class="c-booking-widget__calendar-cell${
  index === this.pendingMonth ? ' is-active' : ''
}" data-month-index="${index}">
            ${month}
          </button>
        `,
  ).join('')}
      </div>
    `;
  }

  renderYearGrid() {
    const anchor = Math.min(
      Math.max(this.visibleYear, this.minYear),
      this.maxYear,
    );
    const startYear = Math.max(this.minYear, anchor - 5);
    const endYear = Math.min(this.maxYear, startYear + 11);
    const years = [];

    for (let year = startYear; year <= endYear; year += 1) {
      years.push(year);
    }

    this.body.innerHTML = `
      <div class="c-booking-widget__calendar-grid c-booking-widget__calendar-grid--years">
        ${years
    .map(
      (year) => `
          <button type="button" class="c-booking-widget__calendar-cell${
  year === this.visibleYear ? ' is-active' : ''
}" data-year-value="${year}">
            ${year}
          </button>
        `,
    )
    .join('')}
      </div>
    `;
  }

  renderDayGrid() {
    const first = new Date(this.visibleYear, this.visibleMonth, 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(
      this.visibleYear,
      this.visibleMonth + 1,
      0,
    ).getDate();
    const cells = [];

    for (let i = 0; i < startWeekday; i += 1) {
      cells.push(
        '<span class="c-booking-widget__calendar-empty" aria-hidden="true"></span>',
      );
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(this.visibleYear, this.visibleMonth, day);
      const iso = DatePicker.formatISO(date);
      const isSelected = this.selectedDate && DatePicker.formatISO(this.selectedDate) === iso;
      const available = this.isAvailable(date);
      cells.push(`
        <button
          type="button"
          class="c-booking-widget__calendar-day${
  isSelected ? ' is-selected' : ''
}"
          data-iso="${iso}"
          ${available ? '' : 'disabled'}
        >${day}</button>
      `);
    }

    this.body.innerHTML = `
      <div class="c-booking-widget__calendar-weekdays">
        ${DatePicker.WEEKDAYS.map((weekday) => `<span>${weekday}</span>`).join(
    '',
  )}
      </div>
      <div class="c-booking-widget__calendar-grid c-booking-widget__calendar-grid--days">
        ${cells.join('')}
      </div>
    `;
  }

  /**
   * Dynamically update the set of available dates and re-render the calendar.
   * Pass `null` to allow all dates (no restriction).
   */
  setAvailableDates(isoSet) {
    this.availableDates = isoSet ?? null;
    if (!this.picker.hidden) {
      this.render();
    }
  }
}
